import React from 'react';
import { AICoachTabProps } from './types';

const AICoachTab: React.FC<AICoachTabProps> = ({
  aiAnalysis,
  aiLoading,
  aiError,
  onGetAIAnalysis,
}) => {
  return (
    <div>
      {!aiAnalysis && !aiLoading && !aiError && (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">Get personalized feedback and tips from our AI pronunciation coach.</p>
          <button
            onClick={onGetAIAnalysis}
            className="py-3 px-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2 mx-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Get AI Analysis
          </button>
        </div>
      )}

      {aiLoading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600">Analyzing your pronunciation with AI...</p>
        </div>
      )}

      {aiError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{aiError}</p>
          <button
            onClick={onGetAIAnalysis}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Try again
          </button>
        </div>
      )}

      {aiAnalysis && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-100">
            <h4 className="text-lg font-semibold text-purple-800 mb-2">Summary</h4>
            <p className="text-gray-700">{aiAnalysis.summary}</p>
          </div>

          {/* Strengths */}
          {aiAnalysis.strengths.length > 0 && (
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <h4 className="text-lg font-semibold text-green-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Strengths
              </h4>
              <ul className="space-y-2">
                {aiAnalysis.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start gap-2 text-green-700">
                    <span className="text-green-500 mt-1">•</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Areas to Improve */}
          {aiAnalysis.areasToImprove.length > 0 && (
            <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-100">
              <h4 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Areas to Improve
              </h4>
              <ul className="space-y-2">
                {aiAnalysis.areasToImprove.map((area, index) => (
                  <li key={index} className="flex items-start gap-2 text-yellow-700">
                    <span className="text-yellow-500 mt-1">•</span>
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tips */}
          {aiAnalysis.tips.length > 0 && (
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <h4 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Practice Tips
              </h4>
              <ul className="space-y-2">
                {aiAnalysis.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2 text-blue-700">
                    <span className="text-blue-500 mt-1">{index + 1}.</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Encouragement */}
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg p-4 border border-pink-100">
            <p className="text-purple-700 italic text-center">{aiAnalysis.encouragement}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AICoachTab;
